import React, { useContext, useEffect, useRef, useState } from 'react';
import { Observer } from 'mobx-react-lite';
import FadeIn from 'react-fade-in';
import momentLocalizer from 'react-widgets-moment';
import { GlobalHotKeys } from 'react-hotkeys';
import moment from 'moment';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModalComponent';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';

import BookingTypeUpdateStore from '../../../../stores/BookingTypeUpdateStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';

import './UpdateBookingType.scss';
import { toJS } from 'mobx';

moment.locale('en');
momentLocalizer();

function UpdateBookingType(props) {
    const isMounted = useRef(true);
    const confirmModalRef = useRef(null);
    const validateRef = useRef(null);
    const bookingTypeUpdate = useContext(BookingTypeUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [bookingType, setBookingType] = useState(null);
    const [newCustomQuestion, setNewCustomQuestion] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        quickDrawerFocus(props.drawer);

        if (bookingTypeUpdate.selectedBookingType) {
            setBookingType(toJS(bookingTypeUpdate.selectedBookingType));
        }

        return () => {
            isMounted.current = false;
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = event => {
        event.preventDefault();

        if (fn.validateForm(validateRef.current)) {
            const index = bookingTypeUpdate.data.findIndex(d => d.id === bookingTypeUpdate.selectedBookingTypeId);

            if (index >= 0 && hasUnsavedChanges) {
                bookingTypeUpdate.data[index] = bookingType;
                bookingTypeUpdate.hasUnsavedChanges = hasUnsavedChanges;
                bookingTypeUpdate.update(true)
                    .then(() => {
                        if (isMounted.current) {
                            if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                                props.onSuccess(event, { updated: true, data: { id: bookingTypeUpdate.id } });
                            }
                        }
                    })
            }
            else if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                props.onSuccess(event);
            }
        }
    }

    const handleDelete = (event) => {
        confirmModalRef.current.show({
            icon: <i className='fal fa-times-square text-danger mr-2'></i>,
            message: <>Continue to <span className='fw-500'>&nbsp;delete the [{bookingType ? bookingType.code : ''}] booking type</span>? </>,
            onOption1Click: handleDeleteConfirm,
            onCancel: handleDeleteCancel,
        })
    }

    const handleDeleteConfirm = (event) => {
        bookingTypeUpdate.delete()
            .then(() => {
                confirmModalRef.current.close();

                if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                    props.onSuccess(event);
                }
            })
    }

    const handleDeleteCancel = (event) => {
        confirmModalRef.current.close();
    }

    const handleUsers = (event) => {
        quickDrawer.activateQuickDrawer('online-booking-type', 'users', null, handleUsersConfirm, handleUsersCancel);
    }

    const handleUsersConfirm = () => { }

    const handleUsersCancel = () => { }

    const handleNameChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.name = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleSubtitleChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.subtitle = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleDescriptionChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.description = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleIsRequestOnlyChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.isRequestOnly = (value === 'Request');
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleIsHiddenChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.isHidden = (value === 'Hidden');
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleAnyoneAvailableEnabledChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.anyoneAvailableEnabled = (value === 'Anyone');
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleAnyoneAvailableTextChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.anyoneAvailableLabel = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleAnyoneAvailablePriorityChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.anyoneAvailablePriority = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleSexQuestionChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.sexQuestion = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleGenderQuestionChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.genderQuestion = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handlePronounQuestionChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.pronounQuestion = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleDateOfBirthQuestionChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.dateOfBirthQuestion = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleHealthCardNumberQuestionChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.healthCardNumberQuestion = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handlePhoneQuestionChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.phoneQuestion = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleIsNewOrReturnQuestionChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.isNewOrReturnQuestion = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleMessageQuestionChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.messageQuestion = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleCustomQuestionAdd = (event) => {
        let question;
        const BookingType = JSON.parse(JSON.stringify(bookingType));

        if (!BookingType.customQuestion3) {
            BookingType.customQuestion3 = null;
            BookingType.customQuestion3Type = 'SingleLine';
            BookingType.customQuestion3Required = false;
            question = 3;
        }
        if (!BookingType.customQuestion2) {
            BookingType.customQuestion2 = null;
            BookingType.customQuestion2Type = 'SingleLine';
            BookingType.customQuestion2Required = false;
            question = 2;
        }
        if (!BookingType.customQuestion1) {
            BookingType.customQuestion1 = null;
            BookingType.customQuestion1Type = 'SingleLine';
            BookingType.customQuestion1Required = false;
            question = 1;
        }

        if (question <= 3) {
            setBookingType(BookingType);
            setNewCustomQuestion(question);
        }
    }

    const handleCustomQuestion1Change = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.customQuestion1 = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleCustomQuestion1TypeChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.customQuestion1Type = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleCustomQuestion1RequiredChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.customQuestion1Required = (value === 'Required');
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleCustomQuestion1Remove = (event) => {
        const BookingType = JSON.parse(JSON.stringify(bookingType));

        if (BookingType.customQuestion2) {
            BookingType.customQuestion1 = BookingType.customQuestion2;
            BookingType.customQuestion1Type = BookingType.customQuestion2Type;
            BookingType.customQuestion1Required = BookingType.customQuestion2Required;
            BookingType.customQuestion2 = null;
            BookingType.customQuestion2Type = null;
            BookingType.customQuestion2Required = null;

            if (BookingType.customQuestion3) {
                BookingType.customQuestion2 = BookingType.customQuestion3;
                BookingType.customQuestion2Type = BookingType.customQuestion3Type;
                BookingType.customQuestion2Required = BookingType.customQuestion3Required;
                BookingType.customQuestion3 = null;
                BookingType.customQuestion3Type = null;
                BookingType.customQuestion3Required = null;
                setNewCustomQuestion(2);
            }
            else {
                setNewCustomQuestion(1)
            }

            setBookingType(BookingType);
            setHasUnsavedChanges(true);
        }
        else if (BookingType.customQuestion3) {
            BookingType.customQuestion1 = BookingType.customQuestion3;
            BookingType.customQuestion1Type = BookingType.customQuestion3Type;
            BookingType.customQuestion1Required = BookingType.customQuestion3Required;
            BookingType.customQuestion3 = null;
            BookingType.customQuestion3Type = null;
            BookingType.customQuestion3Required = null;

            setBookingType(BookingType);
            setHasUnsavedChanges(true);
            setNewCustomQuestion(1)
        }
        else {
            BookingType.customQuestion1 = null;
            BookingType.customQuestion1Type = null;
            BookingType.customQuestion1Required = false;

            setBookingType(BookingType);
            setHasUnsavedChanges(true);
            setNewCustomQuestion(0);
        }
    }

    const handleCustomQuestion2Change = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.customQuestion2 = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleCustomQuestion2TypeChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.customQuestion2Type = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleCustomQuestion2RequiredChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.customQuestion2Required = (value === 'Required');
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleCustomQuestion2Remove = (event) => {
        const BookingType = JSON.parse(JSON.stringify(bookingType));

        if (BookingType.customQuestion3) {
            BookingType.customQuestion2 = BookingType.customQuestion3;
            BookingType.customQuestion2Type = BookingType.customQuestion3Type;
            BookingType.customQuestion2Required = BookingType.customQuestion3Required;
            BookingType.customQuestion3 = null;
            BookingType.customQuestion3Type = null;
            BookingType.customQuestion3Required = null;

            setBookingType(BookingType);
            setHasUnsavedChanges(true);
            setNewCustomQuestion(2);
        }
        else {
            BookingType.customQuestion2 = null;
            BookingType.customQuestion2Type = null;
            BookingType.customQuestion2Required = false;

            setBookingType(BookingType);
            setHasUnsavedChanges(true);
            setNewCustomQuestion(1);
        }
    }

    const handleCustomQuestion3Change = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.customQuestion3 = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleCustomQuestion3TypeChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.customQuestion3Type = value ? value : null;
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleCustomQuestion3RequiredChange = (event) => {
        const { value } = event.target;
        const BookingType = JSON.parse(JSON.stringify(bookingType));
        BookingType.customQuestion3Required = (value === 'Required');
        setBookingType(BookingType);
        setHasUnsavedChanges(true);
    }

    const handleCustomQuestion3Remove = (event) => {
        const BookingType = JSON.parse(JSON.stringify(bookingType));

        BookingType.customQuestion3 = null;
        BookingType.customQuestion3Type = null;
        BookingType.customQuestion3Required = false;

        setBookingType(BookingType);
        setHasUnsavedChanges(true);
        setNewCustomQuestion(2);
    }

    return <>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <Observer>{() =>
                <>
                    {
                        (props.drawer === quickDrawer.drawerOpened) ?
                            <GlobalHotKeys
                                keyMap={{
                                    close: ['esc'],
                                }}
                                handlers={{
                                    close: event => {
                                        handleCancel(event)
                                    },
                                }}
                                allowChanges={true}
                            /> : null
                    }
                    <fieldset disabled={bookingTypeUpdate.isSaving}>
                        <div className='quick-drawer'>
                            <QuickDrawerHeader
                                drawer={props.drawer}
                                icon={oh.getIcon('appointment', 'update')}
                                action='Update'
                                category='Online Booking Type'
                                className='appointments'
                                onCancel={handleCancel}
                            />
                            <div className='quick-drawer-body'>
                                {
                                    bookingTypeUpdate.isReady && bookingType ?
                                        <FadeIn>
                                            <div className='new-online-booking-type body-content'>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <strong className='text-gray-600 text-uppercase'>General Information</strong>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12 validate validate-required'>
                                                            <label className='required'><small>Code</small></label>
                                                            <input
                                                                type='text'
                                                                className='form-control text-uppercase'
                                                                maxLength={15}
                                                                disabled={true}
                                                                value={(bookingType.code ? bookingType.code : '')}
                                                            />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12 validate validate-required'>
                                                            <label className='required'><small>Title</small></label>
                                                            <input
                                                                type='text'
                                                                className='form-control'
                                                                maxLength={150}
                                                                value={(bookingType.name ? bookingType.name : '')}
                                                                onChange={handleNameChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <label><small>Subtitle</small></label>
                                                            <input
                                                                type='text'
                                                                className='form-control'
                                                                maxLength={75}
                                                                value={(bookingType.subtitle ? bookingType.subtitle : '')}
                                                                onChange={handleSubtitleChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12 validate validate-required'>
                                                            <label className='required'><small>Description</small></label>
                                                            <textarea
                                                                className='form-control'
                                                                rows={5}
                                                                maxLength={3000}
                                                                value={(bookingType.description ? bookingType.description : '')}
                                                                onChange={handleDescriptionChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <hr />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <strong className='text-gray-600 text-uppercase'>Booking Preference</strong>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-6'>
                                                            <label><small>Mode</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(!!bookingType.isRequestOnly ? 'Request' : 'TimeSlot')}
                                                                onChange={handleIsRequestOnlyChange}
                                                            >
                                                                <option value='TimeSlot'>Timeslots</option>
                                                                <option value='Request'>Request Only</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-6'>
                                                            <label><small>Visibility</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(!!bookingType.isHidden ? 'Hidden' : 'Public')}
                                                                onChange={handleIsHiddenChange}
                                                            >
                                                                <option value='Public'>Online Booking Page</option>
                                                                <option value='Hidden'>Direct Link Only</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                {
                                                    !bookingType.isRequestOnly ?
                                                        <section className='m-0 mb-4'>
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <label><small>Selection</small></label>
                                                                    <select
                                                                        className='custom-select form-control'
                                                                        value={(!!bookingType.anyoneAvailableEnabled ? 'Anyone' : 'Specific')}
                                                                        onChange={handleAnyoneAvailableEnabledChange}
                                                                    >
                                                                        <option value='Specific'>Specific doctor / staff</option>
                                                                        <option value='Anyone'>Anyone available</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            <div className={'row' + (!!bookingType.anyoneAvailableEnabled ? ' mt-4' : ' d-none')}>
                                                                <div className='col-6'>
                                                                    <label><small>Anyone available label</small></label>
                                                                    <input
                                                                        type='text'
                                                                        className='form-control'
                                                                        value={(bookingType.anyoneAvailableLabel ? bookingType.anyoneAvailableLabel : '')}
                                                                        onChange={handleAnyoneAvailableTextChange}
                                                                    />
                                                                </div>
                                                                <div className='col-6'>
                                                                    <label><small>Anyone available priority</small></label>
                                                                    <select
                                                                        className='custom-select form-control'
                                                                        value={bookingType.anyoneAvailablePriority}
                                                                        onChange={handleAnyoneAvailablePriorityChange}
                                                                    >
                                                                        <option value='DisplayOrder'>Order of display</option>
                                                                        <option value='Availabilities'>Most available</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </section> : null
                                                }
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <hr />
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <strong className='text-gray-600 text-uppercase'>Booking Questions</strong>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-6'>
                                                            <label><small>First Name</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                disabled={true}
                                                                value='Required'
                                                            >
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-6'>
                                                            <label><small>Last Name</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                disabled={true}
                                                                value='Required'
                                                            >
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-4'>
                                                            <label><small>Sex</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingType.sexQuestion ? bookingType.sexQuestion : '')}
                                                                onChange={handleSexQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-4'>
                                                            <label><small>Gender</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingType.genderQuestion ? bookingType.genderQuestion : '')}
                                                                onChange={handleGenderQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-4'>
                                                            <label><small>Pronoun</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingType.pronounQuestion ? bookingType.pronounQuestion : '')}
                                                                onChange={handlePronounQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-6'>
                                                            <label><small>Date of Birth</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingType.dateOfBirthQuestion ? bookingType.dateOfBirthQuestion : '')}
                                                                onChange={handleDateOfBirthQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-6'>
                                                            <label><small>Health Card Number</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingType.healthCardNumberQuestion ? bookingType.healthCardNumberQuestion : '')}
                                                                onChange={handleHealthCardNumberQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-6'>
                                                            <label><small>Email</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                disabled={true}
                                                                value='Required'
                                                            >
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                        <div className='col-6'>
                                                            <label><small>Phone</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingType.phoneQuestion ? bookingType.phoneQuestion : '')}
                                                                onChange={handlePhoneQuestionChange}
                                                            >
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <label><small>New or Return Patient</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingType.isNewOrReturnQuestion ? bookingType.isNewOrReturnQuestion : '')}
                                                                onChange={handleIsNewOrReturnQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section className='m-0 mb-4'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <label><small>Message</small></label>
                                                            <select
                                                                className='custom-select form-control'
                                                                value={(bookingType.messageQuestion ? bookingType.messageQuestion : '')}
                                                                onChange={handleMessageQuestionChange}
                                                            >
                                                                <option value=''>Hidden</option>
                                                                <option value='Optional'>Optional</option>
                                                                <option value='Required'>Required</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </section>
                                                {
                                                    !!bookingType.customQuestion1 || (newCustomQuestion && newCustomQuestion >= 1) ?
                                                        <><section className='m-0 mb-2'>
                                                            <div className='row'>
                                                                <div className='col-12 validate validate-required'>
                                                                    <div className='d-flex align-items-center mb-1'>
                                                                        <label className='flex-1 m-0 required'><small>Custom Question #1</small></label>
                                                                        <button
                                                                            type='button'
                                                                            className='btn btn-icon line-height-1 mr-n2'
                                                                            onClick={handleCustomQuestion1Remove}
                                                                        >
                                                                            <i className='fal fa-times fs-sm text-danger'></i>
                                                                        </button>
                                                                    </div>
                                                                    <input
                                                                        type='text'
                                                                        className='form-control'
                                                                        maxLength={255}
                                                                        value={(bookingType.customQuestion1 ? bookingType.customQuestion1 : '')}
                                                                        onChange={handleCustomQuestion1Change}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </section>
                                                            <section className='m-0 mb-4'>
                                                                <div className='row'>
                                                                    <div className='col-6'>
                                                                        <select
                                                                            className='custom-select form-control'
                                                                            value={(bookingType.customQuestion1Type ? bookingType.customQuestion1Type : '')}
                                                                            onChange={handleCustomQuestion1TypeChange}
                                                                        >
                                                                            <option value='SingleLine'>Single-line Textbox</option>
                                                                            <option value='MultiLine'>Multi-line Textbox</option>
                                                                        </select>
                                                                    </div>
                                                                    <div className='col-6'>
                                                                        <select
                                                                            className='custom-select form-control'
                                                                            value={(!!bookingType.customQuestion1Required ? 'required' : '')}
                                                                            onChange={handleCustomQuestion1RequiredChange}
                                                                        >
                                                                            <option value=''>Optional</option>
                                                                            <option value='Required'>Required</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </section>
                                                        </> : null
                                                }
                                                {
                                                    !!bookingType.customQuestion2 || (newCustomQuestion && newCustomQuestion >= 2) ?
                                                        <><section className='m-0 mb-2'>
                                                            <div className='row'>
                                                                <div className='col-12 validate validate-required'>
                                                                    <div className='d-flex align-items-center mb-1'>
                                                                        <label className='flex-1 m-0 required'><small>Custom Question #2</small></label>
                                                                        <button
                                                                            type='button'
                                                                            className='btn btn-icon line-height-1 mr-n2'
                                                                            onClick={handleCustomQuestion2Remove}
                                                                        >
                                                                            <i className='fal fa-times fs-sm text-danger'></i>
                                                                        </button>
                                                                    </div>
                                                                    <input
                                                                        type='text'
                                                                        className='form-control'
                                                                        maxLength={255}
                                                                        value={(bookingType.customQuestion2 ? bookingType.customQuestion2 : '')}
                                                                        onChange={handleCustomQuestion2Change}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </section>
                                                            <section className='m-0 mb-4'>
                                                                <div className='row'>
                                                                    <div className='col-6'>
                                                                        <select
                                                                            className='custom-select form-control'
                                                                            value={(bookingType.customQuestion2Type ? bookingType.customQuestion2Type : '')}
                                                                            onChange={handleCustomQuestion2TypeChange}
                                                                        >
                                                                            <option value='SingleLine'>Single-line Textbox</option>
                                                                            <option value='MultiLine'>Multi-line Textbox</option>
                                                                        </select>
                                                                    </div>
                                                                    <div className='col-6'>
                                                                        <select
                                                                            className='custom-select form-control'
                                                                            value={(!!bookingType.customQuestion2Required ? 'required' : '')}
                                                                            onChange={handleCustomQuestion2RequiredChange}
                                                                        >
                                                                            <option value=''>Optional</option>
                                                                            <option value='Required'>Required</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </section>
                                                        </> : null
                                                }
                                                {
                                                    !!bookingType.customQuestion3 || (newCustomQuestion && newCustomQuestion >= 3) ?
                                                        <><section className='m-0 mb-2'>
                                                            <div className='row'>
                                                                <div className='col-12 validate validate-required'>
                                                                    <div className='d-flex align-items-center mb-1'>
                                                                        <label className='flex-1 m-0 required'><small>Custom Question #3</small></label>
                                                                        <button
                                                                            type='button'
                                                                            className='btn btn-icon line-height-1 mr-n2'
                                                                            onClick={handleCustomQuestion3Remove}
                                                                        >
                                                                            <i className='fal fa-times fs-sm text-danger'></i>
                                                                        </button>
                                                                    </div>
                                                                    <input
                                                                        type='text'
                                                                        className='form-control'
                                                                        maxLength={255}
                                                                        value={(bookingType.customQuestion3 ? bookingType.customQuestion3 : '')}
                                                                        onChange={handleCustomQuestion3Change}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </section>
                                                            <section className='m-0 mb-4'>
                                                                <div className='row'>
                                                                    <div className='col-6'>
                                                                        <select
                                                                            className='custom-select form-control'
                                                                            value={(bookingType.customQuestion3Type ? bookingType.customQuestion3Type : '')}
                                                                            onChange={handleCustomQuestion3TypeChange}
                                                                        >
                                                                            <option value='SingleLine'>Single-line Textbox</option>
                                                                            <option value='MultiLine'>Multi-line Textbox</option>
                                                                        </select>
                                                                    </div>
                                                                    <div className='col-6'>
                                                                        <select
                                                                            className='custom-select form-control'
                                                                            value={(!!bookingType.customQuestion3Required ? 'required' : '')}
                                                                            onChange={handleCustomQuestion3RequiredChange}
                                                                        >
                                                                            <option value=''>Optional</option>
                                                                            <option value='Required'>Required</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </section>
                                                        </> : null
                                                }
                                                <>
                                                    {
                                                        (!bookingType.customQuestion1 || !bookingType.customQuestion2 || !bookingType.customQuestion3) && newCustomQuestion < 3 ?
                                                            <section className='m-0 mb-4'>
                                                                <div className='row'>
                                                                    <div className='col-12'>
                                                                        <button
                                                                            type='button'
                                                                            className='btn btn-link p-0'
                                                                            onClick={handleCustomQuestionAdd}
                                                                        >
                                                                            Add custom question
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </section> : null
                                                    }
                                                </>
                                            </div>
                                        </FadeIn> : renderQuickDrawerLoading()
                                }
                            </div>
                            <Observer>{() =>
                                bookingTypeUpdate.isReady ?
                                    <div className='quick-drawer-action'>
                                        <div className='row'>
                                            <div className='col-4'>
                                                <button
                                                    type='button'
                                                    className='btn btn-light'
                                                    disabled={hasUnsavedChanges}
                                                    onClick={handleUsers}
                                                >Users</button>
                                            </div>
                                            <div className='col-8'>
                                                <div className='float-right'>
                                                    <button
                                                        type='button'
                                                        className='btn btn-danger'
                                                        onClick={handleDelete}
                                                    >Delete</button>
                                                    <button
                                                        type='submit'
                                                        className='btn btn-success ml-2'
                                                    >Save</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div> : null
                            }</Observer>
                        </div>
                    </fieldset>
                </>
            }</Observer>
        </form>
        <BodyEnd>
            <ConfirmModal ref={confirmModalRef} />
        </BodyEnd>
    </>
}

export default UpdateBookingType;